import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faVideo,
  faTachometer,
  faEye,
  faPlus,
  faTable,
  faArrowRight,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { getCMSVideoListAPI, deleteCMSVideoAPI } from "../../API/CMSVideo";
import { useRecoilState } from "recoil";
import { getVideoStateLabelByValue } from "../../Constants/FieldOptions";
import FormErrorBox from "../Reusable/FormErrorBox";
import PageLoadingContent from "../Reusable/PageLoadingContent";

import { topAlertMessageState, topAlertStatusState } from "../../AppState";

function VideoList() {
  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);

  ////
  //// Component states.
  ////

  const [errors, setErrors] = useState({});
  const [videos, setVideos] = useState("");
  const [selectedVideoForDeletion, setSelectedVideoForDeletion] = useState("");
  const [isFetching, setFetching] = useState(false);

  ////
  //// API.
  ////

  function onVideoListSuccess(response) {
    console.log("onVideoListSuccess: Starting...");
    setVideos(response);
  }

  function onVideoListError(apiErr) {
    console.log("onVideoListError: Starting...");
    setErrors(apiErr);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onVideoListDone() {
    console.log("onVideoListDone: Starting...");
    setFetching(false);
  }

  function onVideoDeleteSuccess(response) {
    console.log("onVideoDeleteSuccess: Starting..."); // For debugging purposes only.

    // Update notification.
    setTopAlertStatus("success");
    setTopAlertMessage("Video deleted");
    setTimeout(() => {
      console.log(
        "onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // Fetch again an updated list.
    fetchList();
  }

  function onVideoDeleteError(apiErr) {
    console.log("onVideoDeleteError: Starting..."); // For debugging purposes only.
    setErrors(apiErr);

    // Update notification.
    setTopAlertStatus("danger");
    setTopAlertMessage("Failed deleting");
    setTimeout(() => {
      console.log(
        "onVideoDeleteError: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onVideoDeleteDone() {
    console.log("onVideoDeleteDone: Starting...");
    setFetching(false);
  }

  ////
  //// Event handling.
  ////

  const fetchList = () => {
    setFetching(true);
    setErrors({});
    getCMSVideoListAPI(onVideoListSuccess, onVideoListError, onVideoListDone);
  };

  // const onSelectVideoForDeletion = (e, video) => {
  //     console.log("onSelectVideoForDeletion", video);
  //     setSelectedVideoForDeletion(video);
  // }

  const onDeselectVideoForDeletion = (e) => {
    console.log("onDeselectVideoForDeletion");
    setSelectedVideoForDeletion("");
  };

  const onDeleteConfirmButtonClick = (e) => {
    console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

    deleteCMSVideoAPI(
      selectedVideoForDeletion.id,
      onVideoDeleteSuccess,
      onVideoDeleteError,
      onVideoDeleteDone
    );
    setSelectedVideoForDeletion("");
  };

  ////
  //// Misc.
  ////

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0);
      fetchList();
    }

        return () => { mounted = false; }
    }, []);

  ////
  //// Component rendering.
  ////

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="is-active">
                <Link to="/cms-videos" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faVideo} />
                  &nbsp;Videos
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/dashboard`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Dashboard
                </Link>
              </li>
            </ul>
          </nav>

          {/* Page */}
          <nav class="box">
            <div class={`modal ${selectedVideoForDeletion ? "is-active" : ""}`}>
              <div class="modal-background"></div>
              <div class="modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">Are you sure?</p>
                  <button
                    class="delete"
                    aria-label="close"
                    onClick={onDeselectVideoForDeletion}
                  ></button>
                </header>
                <section class="modal-card-body">
                  You are about to <strong>permanently delete</strong> this
                  video and all the data inside of it from all sources. This
                  action is cannot be undone. Are you sure you would like to
                  continue?
                </section>
                <footer class="modal-card-foot">
                  <button
                    class="button is-success"
                    onClick={onDeleteConfirmButtonClick}
                  >
                    Confirm
                  </button>
                  <button class="button" onClick={onDeselectVideoForDeletion}>
                    Cancel
                  </button>
                </footer>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <h1 class="title is-4">
                  <FontAwesomeIcon className="fas" icon={faVideo} />
                  &nbsp;Videos
                </h1>
              </div>
              {!isFetching && (
                <div class="column has-text-right">
                  <button
                    class="button is-small is-info is-fullwidth-mobile"
                    onClick={fetchList}
                  >
                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                    <span class="is-hidden-desktop is-hidden-tablet">
                      &nbsp;Refresh
                    </span>
                  </button>
                  &nbsp;
                  <Link
                    to={`/cms-videos/add`}
                    class="button is-small is-success is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faPlus} />
                    &nbsp;New Video
                  </Link>
                </div>
              )}
            </div>
            <FormErrorBox errors={errors} />

            {isFetching ? (
              <>
                <PageLoadingContent displayMessage={"Please wait"} />{" "}
              </>
            ) : (
              <>
                {videos && videos.length > 0 ? (
                  <div class="container">
                    <div class="b-table">
                      <div class="table-wrapper has-mobile-cards">
                        <table class="table is-fullwidth is-striped is-hoverable is-fullwidth">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>State</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {videos &&
                              videos.map(function (video, i) {
                                return (
                                  <tr>
                                    <td data-label="Name">{video.name}</td>
                                    <td data-label="Name">
                                      {getVideoStateLabelByValue(video.state)}
                                    </td>
                                    <td class="is-actions-cell">
                                      <div class="buttons is-right">
                                        <Link
                                          to={`/cms-video/${video.id}`}
                                          class="button is-small is-primary"
                                          type="button"
                                        >
                                          <FontAwesomeIcon
                                            className="mdi"
                                            icon={faEye}
                                          />
                                          &nbsp;View
                                        </Link>
                                        {/*
                                                                        <Link to={`/cms-video/${video.id}/edit`} class="button is-small is-warning" type="button">
                                                                            <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit
                                                                        </Link>
                                                                        <button onClick={(e, ses) => onSelectVideoForDeletion(e, video)} class="button is-small is-danger" type="button">
                                                                            <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete
                                                                        </button>
                                                                        */}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <section class="hero is-medium has-background-white-ter">
                    <div class="hero-body">
                      <p class="title">
                        <FontAwesomeIcon className="fas" icon={faTable} />
                        &nbsp;No Videos
                      </p>
                      <p class="subtitle">
                        No videos.{" "}
                        <b>
                          <Link to="/cms-videos/add">
                            Click here&nbsp;
                            <FontAwesomeIcon
                              className="mdi"
                              icon={faArrowRight}
                            />
                          </Link>
                        </b>{" "}
                        to get started uploading your first video.
                      </p>
                    </div>
                  </section>
                )}
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default VideoList;
